import { useEffect, useState } from 'react';
import { Loader2, Wallet, Phone, Mail, ShieldOff, ShieldCheck, X, AlertTriangle } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';

/**
 * The delinquency worklist. Ordered worst-first because that is the order an
 * operator works it, and it makes the "past grace" boundary the visual break.
 */
export default function DelinquencyPage() {
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [suspending, setSuspending] = useState(null);

  const load = async () => {
    setState('loading');
    try {
      const { items: list } = await api.adminDelinquency(200);
      setItems(list);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (item) => {
    setBusyId(item.lease_public_id);
    setError(null);
    try {
      await api.adminRestoreAccess(item.lease_public_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (state === 'loading') {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>;
  }

  // A failed load must never fall through to the reassuring empty state. "No
  // unpaid invoices" when the API is down is the wrong lie to tell an operator
  // about money owed.
  if (state === 'error') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-rose-400" aria-hidden="true" />
        <p className="mt-3 text-sm font-bold text-slate-900">Couldn&apos;t load the worklist</p>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <p className="mt-1 text-xs text-slate-400">
          This is a load failure, not an empty list — do not read it as &ldquo;nothing owed&rdquo;.
        </p>
        <button type="button" onClick={load} className="btn-ghost mx-auto mt-4">Try again</button>
      </div>
    );
  }

  const owed = items.reduce((sum, i) => sum + i.balance_cents, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Delinquency</h1>
      <p className="mt-1 text-sm text-slate-600">
        {items.length} unpaid invoice{items.length === 1 ? '' : 's'} past due · {formatCents(owed)} outstanding
      </p>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {items.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Wallet className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">Nothing past due</p>
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {items.map((item) => (
          <li key={item.invoice_public_id}
              className={`rounded-xl border p-4 ${item.past_grace ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-white'}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-extrabold text-slate-900">
                    {item.first_name} {item.last_name}
                  </span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    Unit {item.unit_number}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${
                    item.past_grace ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-900'}`}>
                    {item.days_overdue} days overdue
                  </span>
                  {item.access_state === 'suspended' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-[12px] font-bold text-white">
                      <ShieldOff className="h-3 w-3" aria-hidden="true" /> Access suspended
                    </span>
                  )}
                </div>

                <p className="mt-1 text-xs text-slate-600">
                  {item.invoice_number} · due {formatDate(item.due_on)} · {item.facility_name}
                  {!item.past_grace && ` · still within ${item.late_fee_grace_days}-day grace`}
                </p>

                <p className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                  <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1 hover:underline">
                    <Mail className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />{item.email}
                  </a>
                  {item.phone && (
                    <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 hover:underline">
                      <Phone className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />{item.phone}
                    </a>
                  )}
                </p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-xl font-extrabold tabular-nums text-slate-900">{formatCents(item.balance_cents)}</p>
                <p className="text-[12px] text-slate-500">of {formatCents(item.total_cents)}</p>

                <div className="mt-2">
                  {item.access_state === 'suspended' ? (
                    <button type="button" onClick={() => restore(item)} disabled={busyId === item.lease_public_id}
                            className="btn-ghost py-2 text-xs">
                      <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Restore access
                    </button>
                  ) : (
                    <button type="button" onClick={() => setSuspending(item)}
                            disabled={!item.past_grace}
                            title={item.past_grace ? 'Suspend gate access' : 'Still within the grace period'}
                            className="btn-ghost py-2 text-xs disabled:opacity-40">
                      <ShieldOff className="h-3.5 w-3.5" aria-hidden="true" /> Suspend access
                    </button>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {suspending && (
        <SuspendDialog
          item={suspending}
          onClose={() => setSuspending(null)}
          onDone={() => { setSuspending(null); load(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

/**
 * Suspension requires a written reason — the API rejects it otherwise, and the
 * attendant at the gate reads this exact text back to the tenant.
 */
function SuspendDialog({ item, onClose, onDone, onError }) {
  const [reason, setReason] = useState(`Past due ${item.days_overdue} days`);
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.adminSuspendAccess(item.lease_public_id, reason);
      onDone();
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true"
         aria-label="Suspend gate access">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-slate-900">Suspend gate access</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <p className="mb-4 text-sm text-slate-600">
          {item.first_name} {item.last_name} will be refused at the gate of {item.facility_name}.
          Their vehicle stays in unit {item.unit_number}.
        </p>

        <label className="field-label mb-1" htmlFor="reason">Reason (shown to the attendant)</label>
        <input id="reason" required autoFocus className="field-input mb-5" value={reason}
               onChange={(e) => setReason(e.target.value)} maxLength={120} />

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving || !reason.trim()} className="btn-primary flex-1 !bg-rose-600 hover:!bg-rose-700">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Suspend
          </button>
        </div>
      </form>
    </div>
  );
}
